import React, { useState } from 'react';
import { Phone, MapPin, Mail, Clock } from 'lucide-react';
import { useContact, useSiteConfig } from '../../context/CMSContext';
import { useTrackEvent } from '../../context/AnalyticsContext';
import { MapModal } from '../common/MapModal';
import { WhatsAppMenu } from '../common/WhatsAppMenu';

const Contact: React.FC = () => {
  const contact = useContact();
  const siteConfig = useSiteConfig();
  const trackEvent = useTrackEvent();
  const [showMap, setShowMap] = useState(false);

  if (!contact.enabled) return null;

  const { info } = contact;

  return (
    <>
      <section id="contact" className="py-24 bg-slate-900 relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-gold-600/10 rounded-full -translate-y-1/2 translate-x-1/2 blur-3xl" />
        <div className="absolute bottom-0 left-0 w-96 h-96 bg-blue-600/10 rounded-full translate-y-1/2 -translate-x-1/2 blur-3xl" />

        <div className="container mx-auto px-4 relative z-10">
          <div className="max-w-4xl mx-auto text-center">
            <span className="text-gold-500 text-sm font-bold uppercase tracking-[0.2em]">{contact.sectionTagline}</span>
            <h2 className="text-3xl md:text-5xl font-bold text-white mt-3 mb-8">{contact.sectionTitle}</h2>
            <p className="text-slate-400 text-lg md:text-xl mb-12 max-w-2xl mx-auto">{contact.sectionDescription}</p>

            <div className="flex flex-col sm:flex-row items-center justify-center gap-6">
              <a
                href={`tel:${info.phone}`}
                className="w-full sm:w-auto flex items-center justify-center gap-3 bg-white text-slate-900 px-10 py-5 rounded-xl font-bold text-lg hover:bg-slate-100 transition-all transform hover:-translate-y-1 shadow-xl"
              >
                <Phone className="w-6 h-6" />
                致電 {info.phoneDisplay}
              </a>
              <WhatsAppMenu
                variant="button"
                trackAction={() => trackEvent({ action: 'whatsapp_contact', page: 'contact' })}
              />
            </div>

            <div className="mt-16 grid grid-cols-1 md:grid-cols-3 gap-8 text-slate-400 text-sm">
              <button
                onClick={() => setShowMap(true)}
                className="p-6 rounded-lg bg-white/5 border border-white/10 text-left hover:bg-white/10 transition-colors"
              >
                <p className="text-white font-bold mb-2 flex items-center gap-2">
                  <MapPin className="w-4 h-4 text-gold-600" />
                  辦公地址
                </p>
                <p>{info.addressDisplay}</p>
              </button>
              <div className="p-6 rounded-lg bg-white/5 border border-white/10">
                <p className="text-white font-bold mb-2 flex items-center gap-2">
                  <Mail className="w-4 h-4 text-gold-600" />
                  電子郵件
                </p>
                <a href={`mailto:${info.email}`} className="hover:text-white transition-colors">
                  {info.email}
                </a>
              </div>
              <div className="p-6 rounded-lg bg-white/5 border border-white/10">
                <p className="text-white font-bold mb-2 flex items-center gap-2">
                  <Clock className="w-4 h-4 text-gold-600" />
                  服務時間
                </p>
                <p>{info.businessHours}</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <MapModal
        isOpen={showMap}
        onClose={() => setShowMap(false)}
        title={siteConfig.name}
        address={info.addressDisplay}
        mapLink={info.mapLink}
      />
    </>
  );
};

export default Contact;
